import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';

export interface OrganizationData {
  itemId: string;
  isActive: boolean;
  createdDate: string;
  lastUpdatedDate: string;
  createdBy: string;
  lastUpdatedBy: string;
  name: string;
  email?: string;
  code?: string;
  address?: string;
  phoneNumber?: string;
  websiteUrl?: string;
  language?: string | null;
  organizationIds?: string[];
  tags?: string[];
}

export interface CreateOrganizationRequest {
  name: string;
  email: string;
  code: string;
  address: string;
  phoneNumber: string;
  websiteUrl: string;
}

export interface OrganizationResponse {
  data: OrganizationData[];
  totalCount: number;
}

export interface OrganizationFilters {
  name?: string;
  email?: string;
  code?: string;
  isActive?: boolean;
  createdDateFrom?: Date;
  createdDateTo?: Date;
}

export interface OrganizationApiFilter {
  email: string;
  name: string;
  active: boolean;
  inActive: boolean | null;
}

export interface GetOrganizationsPayload {
  page: number;
  pageSize: number;
  filters?: OrganizationFilters;
}

export interface UpdateOrganizationStatusRequest {
  itemId: string;
  isActive: boolean;
}

interface GetOrganizationApiOrganization {
  name: string;
  isEnable: boolean;
  itemId: string;
  createdDate: string;
  lastUpdatedDate: string;
  createdBy: string;
  language: string | null;
  lastUpdatedBy: string;
  organizationIds: string[];
  tags: string[];
  email?: string;
  code?: string;
  address?: string;
  phoneNumber?: string;
  websiteUrl?: string;
}

interface GetOrganizationResponse {
  organization?: GetOrganizationApiOrganization | null;
  errors: unknown;
  isSuccess: boolean;
}

interface GetOrganizationLegacyResponse {
  data?: OrganizationData | OrganizationData[];
}

type GetOrganizationApiResponse =
  | OrganizationData
  | GetOrganizationResponse
  | GetOrganizationLegacyResponse;

const normalizeOrganizationResponse = (
  response: GetOrganizationApiResponse,
  id: string
): OrganizationData => {
  if ('itemId' in response) {
    return response;
  }

  if ('organization' in response && response.organization) {
    return {
      itemId: response.organization.itemId,
      isActive: response.organization.isEnable,
      createdDate: response.organization.createdDate,
      lastUpdatedDate: response.organization.lastUpdatedDate,
      createdBy: response.organization.createdBy,
      lastUpdatedBy: response.organization.lastUpdatedBy,
      name: response.organization.name,
      email: response.organization.email,
      code: response.organization.code,
      address: response.organization.address,
      phoneNumber: response.organization.phoneNumber,
      websiteUrl: response.organization.websiteUrl,
      language: response.organization.language,
      organizationIds: response.organization.organizationIds,
      tags: response.organization.tags,
    };
  }

  if ('data' in response) {
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data[0];
    }

    if (response.data && !Array.isArray(response.data)) {
      return response.data;
    }
  }

  throw new Error(`Organization with id ${id} not found`);
};

const extractOrganizationItems = (response: unknown): unknown[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (response && typeof response === 'object') {
    const body = response as Record<string, unknown>;
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.organizations)) return body.organizations;
    if (Array.isArray(body.items)) return body.items;
  }

  return [];
};

const toOrganizationData = (item: Record<string, unknown>): OrganizationData => ({
  itemId: String(item.itemId ?? item.organization_id ?? item.id ?? ''),
  isActive: Boolean(item.isActive ?? item.isEnable ?? true),
  createdDate: String(item.createdDate ?? ''),
  lastUpdatedDate: String(item.lastUpdatedDate ?? ''),
  createdBy: String(item.createdBy ?? ''),
  lastUpdatedBy: String(item.lastUpdatedBy ?? ''),
  name: String(item.name ?? 'Organization'),
  email: typeof item.email === 'string' ? item.email : undefined,
  code: typeof item.code === 'string' ? item.code : undefined,
  address: typeof item.address === 'string' ? item.address : undefined,
  phoneNumber: typeof item.phoneNumber === 'string' ? item.phoneNumber : undefined,
  websiteUrl: typeof item.websiteUrl === 'string' ? item.websiteUrl : undefined,
  language: typeof item.language === 'string' ? item.language : item.language === null ? null : undefined,
  organizationIds: Array.isArray(item.organizationIds) ? (item.organizationIds as string[]) : undefined,
  tags: Array.isArray(item.tags) ? (item.tags as string[]) : undefined,
});

export const getOrganizations = async (
  _payload: GetOrganizationsPayload,
  config: ConfigurationSettings
): Promise<OrganizationResponse> => {
  const url = `https://${config.paths.baseUrl}${config.paths.iam.getOrganizations}`;
  const res = await clients.get<unknown>(url, config.blocksKey);
  const data = extractOrganizationItems(res)
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map(toOrganizationData);

  return {
    data,
    totalCount: data.length,
  };
};

/**
 * Fetches a single organization by its ID.
 *
 * @param {string} id - The ID of the organization to fetch.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<OrganizationData>} The organization data.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const organization = await getOrganization(id, config);
 */
export const getOrganization = async (
  id: string,
  config: ConfigurationSettings
): Promise<OrganizationData> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const queryParams = new URLSearchParams({
    ProjectKey: config.blocksKey,
    ItemId: id,
  });
  const url = `${baseUrl}${config.paths.iam.getOrganization.replace(':organizationId', id)}`;

  const res = await clients.get<GetOrganizationApiResponse>(
    url,
    config.blocksKey
  );

  return normalizeOrganizationResponse(res, id);
};

/**
 * Creates a new organization with the provided data.
 *
 * @param {CreateOrganizationRequest} data - The organization data to create.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ itemId: string; errors: unknown; isSuccess: boolean }>} The response containing the itemId, any errors, and the success status.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await createOrganization(organizationData, config);
 */
export const createOrganization = async (
  data: CreateOrganizationRequest,
  config: ConfigurationSettings
): Promise<{ itemId: string; errors: unknown; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.organizationSave}`;
  
  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(data));
};

/**
 * Updates an existing organization with the provided data.
 *
 * @param {string} id - The ID of the organization to update.
 * @param {Partial<CreateOrganizationRequest>} data - The organization data to update.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ itemId: string; errors: unknown; isSuccess: boolean }>} The response containing the itemId, any errors, and the success status.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateOrganization(id, organizationData, config);
 */
export const updateOrganization = async (
  id: string,
  data: Partial<CreateOrganizationRequest>,
  config: ConfigurationSettings
): Promise<{ itemId: string; errors: unknown; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.organizationUpdate}`;
  
  const payload = {
    itemId: id,
    ...data
  };
  
  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(payload));
};

/**
 * Updates the status (active/inactive) of an organization.
 *
 * @param {UpdateOrganizationStatusRequest} data - The organization status update data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ itemId: string; errors: unknown; isSuccess: boolean }>} The response containing the itemId, any errors, and the success status.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateOrganizationStatus({ itemId: "123", isActive: false }, config);
 */
export const updateOrganizationStatus = async (
  data: UpdateOrganizationStatusRequest,
  config: ConfigurationSettings
): Promise<{ itemId: string; errors: unknown; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.businessService.organizationUpdateStatus}`;
  
  const payload = {
    ItemId: data.itemId,
    IsActive: data.isActive
  };
  
  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(payload));
}; 
